import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import billingAddonApi, { BillingAddon } from '../../utils/api/billingAddon';

interface Props {
  divisionId: number;
  tenantId?: number;
}

const StatusBadge: React.FC<{ status: 'active' | 'inactive' }> = ({ status }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      background: status === 'active' ? '#1D9E75' : '#888',
      color: '#fff',
    }}
  >
    {status === 'active' ? 'Active' : 'Inactive'}
  </span>
);

const DivisionBillingAddons: React.FC<Props> = ({ divisionId }) => {
  const [assigned, setAssigned] = useState<BillingAddon[]>([]);
  const [available, setAvailable] = useState<BillingAddon[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAddonId, setSelectedAddonId] = useState<number | ''>('');
  const [busy, setBusy] = useState(false);
  const [removeWarning, setRemoveWarning] = useState<{ addon: BillingAddon; formCount: number } | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const [ass, avail] = await Promise.all([
        billingAddonApi.division.index(divisionId),
        billingAddonApi.division.available(divisionId),
      ]);
      setAssigned(ass);
      setAvailable(avail);
    } catch {
      toast.error('Failed to load billing add-ons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [divisionId]);

  const handleAssign = async () => {
    if (!selectedAddonId) return;
    setBusy(true);
    try {
      await billingAddonApi.division.assign(divisionId, Number(selectedAddonId));
      setSelectedAddonId('');
      await load();
      toast.success('Add-on assigned');
    } catch (err) {
      toast.error((err as any)?.response?.data?.error || 'Failed to assign add-on');
    } finally {
      setBusy(false);
    }
  };

  const handleRemoveClick = async (addon: BillingAddon) => {
    const { formCount } = await billingAddonApi.division.removeImpact(divisionId, addon.id);
    if (formCount > 0) {
      setRemoveWarning({ addon, formCount });
    } else {
      await doRemove(addon.id);
    }
  };

  const doRemove = async (addonId: number) => {
    setBusy(true);
    try {
      await billingAddonApi.division.remove(divisionId, addonId);
      setRemoveWarning(null);
      await load();
      toast.success('Add-on removed');
    } catch {
      toast.error('Failed to remove add-on');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p style={{ padding: 16 }}>Loading…</p>;

  return (
    <div style={{ padding: '16px 0' }}>
      {/* Assign selector */}
      {available.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 20 }}>
          <select
            value={selectedAddonId}
            onChange={e => setSelectedAddonId(e.target.value ? Number(e.target.value) : '')}
            style={{ padding: '6px 10px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13, minWidth: 200 }}
          >
            <option value="">Select add-on to assign…</option>
            {available.map(a => (
              <option key={a.id} value={a.id}>{a.name} ({a.code})</option>
            ))}
          </select>
          <button
            onClick={handleAssign}
            disabled={!selectedAddonId || busy}
            style={{
              background: '#1D9E75', color: '#fff', border: 'none',
              padding: '6px 16px', borderRadius: 4, cursor: selectedAddonId ? 'pointer' : 'not-allowed',
              fontSize: 13, fontWeight: 600, opacity: selectedAddonId ? 1 : 0.5,
            }}
          >
            Assign
          </button>
        </div>
      )}

      {/* Assigned list */}
      {assigned.length === 0 ? (
        <p style={{ color: '#999', fontSize: 13 }}>No add-ons assigned to this division.</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #eee', textAlign: 'left' }}>
              <th style={{ padding: '6px 8px' }}>Name</th>
              <th style={{ padding: '6px 8px' }}>Code</th>
              <th style={{ padding: '6px 8px' }}>Status</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {assigned.map(addon => (
              <tr
                key={addon.id}
                style={{
                  borderBottom: '1px solid #f0f0f0',
                  opacity: addon.status === 'inactive' ? 0.6 : 1,
                }}
              >
                <td style={{ padding: '6px 8px', fontWeight: 500 }}>{addon.name}</td>
                <td style={{ padding: '6px 8px', color: '#555' }}>{addon.code}</td>
                <td style={{ padding: '6px 8px' }}>
                  <StatusBadge status={addon.status} />
                </td>
                <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                  <button
                    onClick={() => handleRemoveClick(addon)}
                    disabled={busy}
                    style={{
                      background: 'none', border: '1px solid #ccc', padding: '3px 10px',
                      borderRadius: 4, cursor: 'pointer', fontSize: 12, color: '#555',
                    }}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Remove warning modal */}
      {removeWarning && (
        <div style={overlayStyle}>
          <div style={dialogStyle}>
            <h3 style={{ marginTop: 0 }}>Remove Add-on</h3>
            <p style={{ fontSize: 13 }}>
              <strong>{removeWarning.addon.name}</strong> is currently assigned to{' '}
              <strong>{removeWarning.formCount}</strong> form{removeWarning.formCount !== 1 ? 's' : ''} in this division.
              Removing the division assignment will not affect existing form assignments,
              but users will not be able to add it to new forms.
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setRemoveWarning(null)} style={cancelBtnStyle}>Cancel</button>
              <button
                onClick={() => doRemove(removeWarning.addon.id)}
                disabled={busy}
                style={removeBtnStyle}
              >
                Remove Anyway
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const overlayStyle: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
};
const dialogStyle: React.CSSProperties = {
  background: '#fff', borderRadius: 6, padding: 24, width: 440,
  maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
};
const cancelBtnStyle: React.CSSProperties = {
  background: 'none', border: '1px solid #ccc', padding: '6px 16px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13,
};
const removeBtnStyle: React.CSSProperties = {
  background: '#c0392b', color: '#fff', border: 'none', padding: '6px 16px',
  borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};

export default DivisionBillingAddons;
